import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import usePublishedBlogs from "../../hooks/usePublishedBlogs";
import JsonLd from "./JsonLd";
import FAQSchema from "./FAQSchema";
import pageFaqs from "../../data/pageFaqs";

const SITE_URL = "https://commmmuniti.com";
const ORGANIZATION_ID = `${SITE_URL}/#organization`;
const LOCAL_BUSINESS_ID = `${SITE_URL}/#localbusiness`;
const WEBSITE_ID = `${SITE_URL}/#website`;

const SERVICES = [
    { id: "seo", name: "SEO Services in Indore", type: "Search Engine Optimization" },
    { id: "ppc", name: "PPC and Google Ads Services in Indore", type: "PPC and Google Ads" },
    { id: "social-media", name: "Social Media Marketing in Indore", type: "Social Media Marketing" },
    { id: "performance-marketing", name: "Performance Marketing in Indore", type: "Performance Marketing" },
    { id: "content-marketing", name: "Content Marketing Services in Indore", type: "Content Marketing" },
    { id: "website-design", name: "Website Design Services in Indore", type: "Website Design and Development" },
];

const SOCIAL_PROFILES = [
    "https://www.linkedin.com/company/commmmunitimedia-marketing/",
    "https://www.instagram.com/commmmuniti/",
    "https://x.com/commmmunitimm",
    "https://in.pinterest.com/commmmuniti",
    "https://www.youtube.com/@OurStrengthStories",
    "https://share.google/fx4zbUCZMMgVciS5H",
];

const PAGE_DETAILS = {
    "/": {
        type: "WebPage",
        title: "Digital Marketing Agency in Indore | Commmmuniti Media Marketing",
        name: "Digital Marketing Agency in Indore | Commmmuniti Media Marketing",
        description:
            "Grow your business with Commmmuniti, a digital marketing agency in Indore offering SEO, Google Ads, social media marketing and website design services.",
        keywords: [
            "digital marketing agency in Indore",
            "best digital marketing company Indore",
            "digital marketing Indore",
            "performance marketing agency Indore",
            "lead generation company Indore",
        ],
    },
    "/about": {
        type: "AboutPage",
        title: "About Commmmuniti Media Marketing | Digital Marketing Agency Indore",
        name: "About Commmmuniti Media Marketing | Digital Marketing Agency Indore",
        description:
            "Meet the strategists, designers and marketers behind Commmmuniti, a digital marketing agency in Indore focused on creative, measurable business growth.",
        keywords: ["digital marketing agency in Indore", "best digital marketing company Indore"],
    },
    "/services": {
        type: "CollectionPage",
        title: "Digital Marketing Services in Indore | Commmmuniti Media Marketing",
        name: "Digital Marketing Services in Indore | Commmmuniti Media Marketing",
        description:
            "Explore SEO, PPC, Google Ads, social media marketing, performance marketing, content and website design services from our Indore digital agency.",
        keywords: [
            "SEO services in Indore",
            "social media marketing Indore",
            "PPC agency Indore",
            "Google Ads agency Indore",
            "website design company Indore",
            "content marketing services Indore",
            "local SEO Indore",
            "email marketing services Indore",
            "influencer marketing agency Indore",
        ],
    },
    "/blog": {
        type: "CollectionPage",
        title: "Digital Marketing Blog & SEO Insights | Commmmuniti Media Marketing",
        name: "Digital Marketing Blog & SEO Insights | Commmmuniti Media Marketing",
        description:
            "Read expert insights on SEO, Google Ads, social media, branding, content and website growth from Commmmuniti, a digital marketing agency in Indore.",
        keywords: [
            "digital marketing Indore",
            "SEO company Indore",
            "social media marketing Indore",
            "Google Ads agency Indore",
        ],
    },
};

function makeBreadcrumb(items) {
    return {
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            item: `${SITE_URL}${item.path}`,
        })),
    };
}

export default function SiteSchema() {
    const { pathname } = useLocation();
    const publishedBlogs = usePublishedBlogs();
    const blog = pathname.startsWith("/blog/")
        ? publishedBlogs.find((item) => `/blog/${item.id}` === pathname)
        : null;
    const page = useMemo(
        () =>
            blog
                ? {
                      type: "WebPage",
                      name: blog.seo_title || blog.title,
                      description: blog.meta_description || blog.excerpt,
                      keywords: [...(blog.tags || []), blog.category].filter(Boolean),
                  }
                : PAGE_DETAILS[pathname],
        [blog, pathname]
    );

    useEffect(() => {
        let meta = document.querySelector('meta[name="keywords"]');
        if (!page) {
            meta?.remove();
            return;
        }
        if (!meta) {
            meta = document.createElement("meta");
            meta.name = "keywords";
            document.head.appendChild(meta);
        }
        meta.content = page.keywords.join(", ");
    }, [page]);

    useEffect(() => {
        const staticPage = PAGE_DETAILS[pathname];
        if (!staticPage) return;

        document.title = staticPage.title;
        let metaDescription = document.querySelector(
            'meta[name="description"]',
        );
        if (!metaDescription) {
            metaDescription = document.createElement("meta");
            metaDescription.name = "description";
            document.head.appendChild(metaDescription);
        }
        metaDescription.content = staticPage.description;
    }, [pathname]);

    const schema = useMemo(() => {
        const graph = [
            {
                "@type": "Organization",
                "@id": ORGANIZATION_ID,
                name: "Commmmuniti Media Marketing",
                alternateName: "Commmmuniti",
                url: SITE_URL,
                logo: `${SITE_URL}/communiti-logo.webp`,
                image: `${SITE_URL}/digital-marketing-agency-indore-team.webp`,
                email: "commmmuniti@gmail.com",
                telephone: "+91-7987924110",
                location: { "@id": LOCAL_BUSINESS_ID },
                sameAs: SOCIAL_PROFILES,
                knowsAbout: SERVICES.map((service) => service.type),
            },
            {
                "@type": "LocalBusiness",
                "@id": LOCAL_BUSINESS_ID,
                name: "Commmmuniti Media Marketing",
                alternateName: "Commmmuniti",
                url: SITE_URL,
                logo: `${SITE_URL}/communiti-logo.webp`,
                image: `${SITE_URL}/digital-marketing-agency-indore-team.webp`,
                email: "commmmuniti@gmail.com",
                telephone: "+91-7987924110",
                address: {
                    "@type": "PostalAddress",
                    addressLocality: "Indore",
                    addressRegion: "Madhya Pradesh",
                    addressCountry: "IN",
                },
                geo: {
                    "@type": "GeoCoordinates",
                    latitude: 22.701122446797346,
                    longitude: 75.85226876691303,
                },
                areaServed: { "@type": "City", name: "Indore" },
                sameAs: SOCIAL_PROFILES,
                parentOrganization: { "@id": ORGANIZATION_ID },
            },
            {
                "@type": "WebSite",
                "@id": WEBSITE_ID,
                url: SITE_URL,
                name: "Commmmuniti Media Marketing",
                alternateName: "Commmmuniti",
                publisher: { "@id": ORGANIZATION_ID },
                inLanguage: "en-IN",
            },
            ...SERVICES.map((service) => ({
                "@type": "Service",
                "@id": `${SITE_URL}/services#${service.id}`,
                name: service.name,
                serviceType: service.type,
                url: `${SITE_URL}/services`,
                provider: { "@id": LOCAL_BUSINESS_ID },
                areaServed: { "@type": "City", name: "Indore" },
                availableChannel: {
                    "@type": "ServiceChannel",
                    serviceUrl: `${SITE_URL}/services`,
                },
            })),
        ];

        if (page) {
            const url = `${SITE_URL}${pathname === "/" ? "" : pathname}`;
            graph.push({
                "@type": page.type,
                "@id": `${url}#webpage`,
                url,
                name: page.name,
                description: page.description,
                keywords: page.keywords.join(", "),
                isPartOf: { "@id": WEBSITE_ID },
                about: { "@id": ORGANIZATION_ID },
                ...(pathname === "/services"
                    ? {
                          mainEntity: SERVICES.map((service) => ({
                              "@id": `${SITE_URL}/services#${service.id}`,
                          })),
                      }
                    : {}),
                inLanguage: "en-IN",
            });
        }

        if (pathname !== "/" && page) {
            const items = [{ name: "Home", path: "/" }];
            if (blog) items.push({ name: "Blog", path: "/blog" });
            items.push({ name: blog?.title || page.name, path: pathname });
            graph.push(makeBreadcrumb(items));
        }

        return { "@context": "https://schema.org", "@graph": graph };
    }, [blog, page, pathname]);

    const pageUrl = `${SITE_URL}${pathname === "/" ? "" : pathname}`;
    const pageFaqItems = pageFaqs[pathname] || [];
    const faqSchemaId = `page-faq-${pathname.replace(/[^a-z0-9]+/gi, "-") || "home"}`;

    return (
        <>
            <JsonLd id="site" data={schema} />
            <FAQSchema
                id={faqSchemaId}
                items={pageFaqItems}
                pageUrl={pageUrl}
            />
        </>
    );
}
